/* eslint-disable react/display-name */
import React, { ReactElement, useCallback, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import {
  Button,
  Container,
  Divider,
  makeStyles,
  Step as StepperStep,
  Stepper,
  StepLabel,
  Typography
} from '@material-ui/core';
import { ProductCardValidationProvider } from 'components/@common/ProductLicenses/product-card-validation-provider';
import { Formik, FormikProps, FormikValues, FormikTouched } from 'formik';
import GET_CLASSROOMS_AND_ORGANIZATIONS from 'graph/composite/get-classroom-and-organizations-query';
import CREATE_STUDENTS from 'graph/student/create-students-mutation';
import useParseActionRouteParams from 'hooks/route-action/use-parse-action-route-params';
import {
  SourceKind,
  StudentLoginType,
  Student,
  UserClassroomEnrollment
} from 'types/schema-generated-types';
import maybeGetExternalId from 'utilities/external-id-util';
import * as Yup from 'yup';
import useDefaultGroupSettings from '../../../hooks/use-default-group-settings';
import { AddStudentsForm, Form, LicenseAssignFn, LicenseUnassignFn } from '../../../types';
import DEFAULT_FORM from '../../../util/default-form';
import isValidStudent from '../../../util/is-valid-student';
import {
  AddStudentsStep,
  AssignProductsStep,
  ConfirmationStep,
  formReducer,
  ActionType as FormActionType
} from '../../components/Steps';
import getStudentValidation from '../utils/student-form-validation';

const labels: { [key: string]: string } = {
  ADD_STUDENTS: 'ADD STUDENTS',
  ASSIGN_PRODUCTS: 'ASSIGN PRODUCTS',
  REVIEW: 'REVIEW & CONFIRM'
};

export const getSteps = (type?: SourceKind, usernames?: string[]) => [
  {
    id: 'ADD_STUDENTS',
    validate: getStudentValidation(type, usernames),
    validateOnChange: true,
    validateOnBlur: true
  },
  {
    id: 'ASSIGN_PRODUCTS',
    validationSchema: Yup.object({ students: Yup.array() })
  },
  { id: 'REVIEW' }
];

export const defaultForm: AddStudentsForm = {
  CREATE_CLASS: {
    name: '',
    organization: 'org1',
    educator: 'edu1',
    schoolYear: '2020-2021A',
    secretCodeType: StudentLoginType.LETTER_CARDS,
    color: '#f44336',
    coeducators: ['']
  },
  ADD_STUDENTS: {
    students: new Array(10).fill({
      firstName: undefined,
      lastName: undefined,
      username: undefined,
      email: undefined,
      secretCode: undefined,
      gradeLevel: undefined,
      group: undefined
    })
  }
};

interface AddStudentsModalProps {
  onClose: (success?: boolean) => void;
}

const AddStudentsModal = ({ onClose }: AddStudentsModalProps) => {
  const classes = useStyles();
  const { t } = useTranslation('AddStudentsModal');
  const actionRouteParams = useParseActionRouteParams();
  const params = useParams<any>();
  const { getProductCurriculumDefaultGroup } = useDefaultGroupSettings();

  const classroomId = +(actionRouteParams.classroomId ?? params.classroomId);

  const { data, loading } = useQuery(GET_CLASSROOMS_AND_ORGANIZATIONS, {
    variables: { where: { id: classroomId } }
  });

  const [createStudents, { error }] = useMutation(CREATE_STUDENTS);

  const educator = data?.classroom.users.find((x: UserClassroomEnrollment) =>
    x.classroomRoles.some(role => role.name === 'EDUCATOR')
  );

  const coeducators = data?.classroom.users
    .filter((x: UserClassroomEnrollment) => x.userId !== educator.userId)
    .map((x: UserClassroomEnrollment) => ({
      id: x.user.id,
      email: x.user.username,
      firstName: x.user.firstName,
      lastName: x.user.lastName
    }));

  const usernames = useMemo(() => data?.classroom.students.map((x: Student) => x.username), [data]);

  const steps = getSteps(data?.organizations[0].externalId?.sourceKind, usernames);

  const CREATE_CLASS = {
    name: data?.classroom.name,
    educator: educator?.userId,
    organization: data?.classroom.organizationId,
    color: data?.classroom.color,
    secretCodeType: data?.classroom.loginType,
    schoolYear: data?.classroom.academicSessionId,
    coeducators: coeducators || []
  };

  const [state, dispatch] = useReducer(formReducer, {
    activeStep: 0,
    stepCount: 3,
    form: DEFAULT_FORM
  });

  const { activeStep, form } = state;

  const currentStep = useMemo(() => steps[activeStep], [activeStep, steps]);
  const isFirstStep = useMemo(() => activeStep === 0, [activeStep]);
  const isLastStep = useMemo(() => activeStep === steps.length - 1, [activeStep, steps]);

  const closeModal = useCallback(
    (success?: boolean): void => {
      dispatch({ type: FormActionType.RESET_FORM });
      onClose(success);
    },
    [dispatch, onClose]
  );

  const formikStepProps = useMemo(() => {
    const selectedForm = isLastStep ? form : (form.ADD_STUDENTS as Form);
    const onSubmit = async () => {
      const students = form.ADD_STUDENTS.students
        .filter(isValidStudent)
        .map(({ secretCode, id, ...student }) => ({
          ...student,
          classroomId: [classroomId],
          organizationId: +CREATE_CLASS.organization,
          ...(secretCode
            ? { loginType: secretCode.loginType, password: secretCode.code }
            : { loginType: CREATE_CLASS.secretCodeType, password: '' }),
          ...maybeGetExternalId({
            candidateSourceId: student.email as string,
            entities: data.organizations
          }),
          group: student.group && [student.group],
          licenses: student.licenses?.map(x => ({ productCurriculumId: x.productCurriculumId }))
        }));

      await createStudents({ variables: { students } });
    };

    return {
      initialValues: { ...selectedForm, CREATE_CLASS },
      onSubmit,
      ...currentStep
    };
  }, [currentStep, form, isLastStep, CREATE_CLASS, classroomId, createStudents, data]);

  if (loading) return null;

  return (
    <Formik enableReinitialize {...formikStepProps}>
      {({
        isSubmitting,
        isValid,
        values,
        validateForm,
        setErrors,
        setTouched,
        submitForm
      }: FormikProps<FormikValues>): ReactElement => {
        const handleCancelClick = async (): Promise<void> => {
          if (isFirstStep) {
            closeModal();
            return;
          }

          let newForm = { ...form, ADD_STUDENTS: values };

          if (isLastStep) {
            const validationErrors = await validateForm();
            if (validationErrors && Object.keys(validationErrors).length) {
              setErrors(validationErrors);
              setTouched(validationErrors as FormikTouched<FormikValues>);
              return;
            }

            newForm = { ...(values as AddStudentsForm) };
          }

          dispatch({ type: FormActionType.GO_BACK, payload: { form: newForm } });
        };

        const handleForwardClick = async (): Promise<void> => {
          if (isLastStep) {
            dispatch({ type: FormActionType.UPDATE_FORM, payload: { form: { ...values } } });
            await submitForm();
            if (!error) closeModal(true);
          } else {
            const validationErrors = await validateForm();
            if (validationErrors && Object.keys(validationErrors).length) {
              setErrors(validationErrors);
              setTouched(validationErrors as FormikTouched<FormikValues>);
              return;
            }

            dispatch({
              type: FormActionType.GO_FORWARD,
              payload: { form: { ...form, ADD_STUDENTS: values } }
            });
          }
        };

        return (
          <div className={classes.root}>
            <div className={classes.body}>
              <div className={classes.header}>
                <Typography variant="h2" component="h1">
                  {t('Add Students')}
                </Typography>
              </div>
              <Container>
                <Stepper activeStep={activeStep} className={classes.stepper}>
                  {steps.map(step => {
                    return (
                      <StepperStep key={step.id}>
                        <StepLabel
                          StepIconProps={{
                            classes: {
                              active: classes.stepIcon,
                              completed: classes.stepIcon
                            }
                          }}
                          classes={{
                            label: classes.stepLabel,
                            active: classes.stepLabelActive
                          }}
                        >
                          {t(labels[step.id])}
                        </StepLabel>
                      </StepperStep>
                    );
                  })}
                </Stepper>
              </Container>
              <Divider />
              <div data-testid="stepper-body">
                {((): React.ReactElement | null => {
                  const handleLicenseAssign: LicenseAssignFn = (
                    productFamilyId,
                    productCurriculumId,
                    studentId
                  ) => {
                    dispatch({
                      type: FormActionType.ASSIGN_LICENSE,
                      payload: { productFamilyId, productCurriculumId, studentId }
                    });

                    const gradeLevel = form.ADD_STUDENTS.students[studentId]?.gradeLevel ?? '';
                    const group = getProductCurriculumDefaultGroup(
                      [productCurriculumId],
                      gradeLevel
                    );

                    if (group) {
                      dispatch({
                        type: FormActionType.ADD_GROUP,
                        payload: { studentId, group }
                      });
                    }
                  };

                  const handleLicenseUnassign: LicenseUnassignFn = (
                    productFamilyId,
                    productCurriculumId,
                    studentId
                  ) => {
                    dispatch({
                      type: FormActionType.UNASSIGN_LICENSE,
                      payload: { productFamilyId, productCurriculumId, studentId }
                    });

                    const gradeLevel = form.ADD_STUDENTS.students[studentId]?.gradeLevel ?? '';
                    const group = getProductCurriculumDefaultGroup(
                      [productCurriculumId],
                      gradeLevel
                    );

                    if (group?.name === form.ADD_STUDENTS.students[studentId]?.group?.name) {
                      dispatch({
                        type: FormActionType.REMOVE_GROUP,
                        payload: { studentId }
                      });
                    }
                  };

                  switch (activeStep) {
                    case 0:
                      return <AddStudentsStep index={1} usernames={usernames} />;
                    case 1:
                      return (
                        <AssignProductsStep
                          index={2}
                          organizationId={data?.organizations[0].id}
                          onLicenseAssign={handleLicenseAssign}
                          onLicenseUnassign={handleLicenseUnassign}
                        />
                      );
                    case 2:
                      return (
                        <ConfirmationStep
                          index={3}
                          classFormDisabled
                          organizations={data?.organizations ?? []}
                          onLicenseAssign={handleLicenseAssign}
                          onLicenseUnassign={handleLicenseUnassign}
                        />
                      );
                    default:
                      return null;
                  }
                })()}
              </div>
            </div>
            <div className={classes.footer}>
              <Button
                aria-label={t('Cancel Classroom Create')}
                className={classes.cancelButton}
                variant="outlined"
                color="secondary"
                disableElevation
                onClick={handleCancelClick}
              >
                {isFirstStep ? t('Cancel') : t('Back')}
              </Button>
              <Button
                aria-label={t('Step Forward')}
                className={classes.nextButton}
                variant="contained"
                color="primary"
                disableElevation
                onClick={handleForwardClick}
                disabled={isSubmitting || !isValid}
              >
                {isLastStep ? t('Confirm & Add Students') : t('Next Step')}
              </Button>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

const AddStudentsModalWrapper = (props: AddStudentsModalProps) => {
  return (
    <ProductCardValidationProvider>
      <AddStudentsModal {...props} />
    </ProductCardValidationProvider>
  );
};

const useStyles = makeStyles(theme => {
  const STEPPER_HEIGHT = 24;
  const FOOTER_HEIGHT = 80;

  return {
    root: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      justifyContent: 'center',
      alignItems: 'center',
      display: 'flex',
      minHeight: 100
    },
    stepper: {
      height: STEPPER_HEIGHT,
      paddingTop: 0
    },
    stepLabel: {
      fontWeight: 600
    },
    stepLabelActive: {
      fontWeight: 'unset',
      color: `${theme.palette.secondary.main} !important`
    },
    stepIcon: {
      color: `${theme.palette.secondary.main} !important`
    },
    body: {
      height: `calc(100% - ${FOOTER_HEIGHT}px)`,
      overflow: 'auto'
    },
    footer: {
      width: '100%',
      justifyContent: 'flex-end',
      alignItems: 'center',
      display: 'flex',
      minHeight: FOOTER_HEIGHT,
      borderTop: `1px solid ${theme.palette.divider}`
    },
    nextButton: {
      marginRight: theme.spacing(6)
    },
    cancelButton: {
      marginRight: theme.spacing(3)
    }
  };
});

export default AddStudentsModalWrapper;
